import { useEffect, useState } from 'react';
import { HeaderContent, HEADER_TITLE_CLASS } from '../components/shell/app-header';
import { deleteRef, listRef, saveRef, type RefRow } from '../lib/curriculum';
import { cn } from '@/lib/utils';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { useIsAdminViewer } from '../hooks/use-is-admin-viewer';

type Table = 'cycles' | 'subjects' | 'levels';
const TABS: Array<{ key: Table; label: string }> = [
  { key: 'cycles', label: 'Cycles' },
  { key: 'subjects', label: 'Disciplines' },
  { key: 'levels', label: 'Niveaux' },
];

const inputClass = "py-[5px] px-[7px] text-[12px]";

const GRID: Record<Table, string> = {
  cycles: 'grid-cols-[110px_1fr_56px_30px]',
  subjects: 'grid-cols-[130px_1fr_110px_56px_30px]',
  levels: 'grid-cols-[90px_1fr_130px_56px_30px]',
};

/* CRUD du référentiel curriculaire (cycles / disciplines / niveaux). Admin RLS. */
export default function AdminCurriculumRef() {
  const isAdminViewer = useIsAdminViewer();
  const [tab, setTab] = useState<Table>('subjects');
  const [rows, setRows] = useState<RefRow[]>([]);
  const [cycles, setCycles] = useState<RefRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<Partial<RefRow>>({});

  const reload = async () => {
    setError(null);
    try {
      const [r, c] = await Promise.all([listRef(tab), tab === 'levels' ? listRef('cycles') : Promise.resolve([])]);
      setRows(r); if (tab === 'levels') setCycles(c); setDraft({});
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };
  /* eslint-disable-next-line react-hooks/exhaustive-deps -- reload stable, run on [tab] */
  useEffect(() => { void reload(); }, [tab]);

  const guard = async (fn: () => Promise<void>) => {
    setBusy(true); setError(null);
    try { await fn(); await reload(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <HeaderContent width="lg" showBurger backTo="/admin/curriculum" backLabel="Programmes">
        <h1 className={HEADER_TITLE_CLASS}>Référentiel</h1>
      </HeaderContent>
      <main className="max-w-[920px] mx-auto px-4 pb-12 pt-4">
        <div className="flex gap-[6px] mb-3">
          {TABS.map((t) => (
            <Button key={t.key} variant={tab === t.key ? 'default' : 'outline'} onClick={() => setTab(t.key)}>
              {t.label}
            </Button>
          ))}
        </div>
        {error && <Alert>{error}</Alert>}

        <div className="border border-border rounded-[12px] overflow-hidden bg-card">
          <RowHead tab={tab} />
          {rows.map((r) => (
            <RowEdit key={r.id} tab={tab} row={r} cycles={cycles} busy={busy} readOnly={!isAdminViewer}
              onSave={(f) => guard(() => saveRef(tab, { id: r.id, ...f }))}
              onDelete={() => { if (confirm(`Supprimer « ${r.label} » ?`)) void guard(() => deleteRef(tab, r.id)); }} />
          ))}
          {isAdminViewer && (
            <div className={cn("grid gap-[6px] py-[5px] px-[10px] items-center border-b border-naia-line-soft min-w-[440px] bg-secondary", GRID[tab])}>
              <Input placeholder="code" value={draft.code ?? ''} onChange={(e) => setDraft({ ...draft, code: e.target.value })} className={inputClass} />
              <Input placeholder="libellé" value={draft.label ?? ''} onChange={(e) => setDraft({ ...draft, label: e.target.value })} className={inputClass} />
              {tab === 'subjects' && (
                <NativeSelect value={draft.nature ?? 'discipline'} onChange={(e) => setDraft({ ...draft, nature: e.target.value })} className={inputClass}>
                  <option value="discipline">discipline</option><option value="transversal">transversal</option>
                </NativeSelect>
              )}
              {tab === 'levels' && (
                <NativeSelect value={draft.cycle_id ?? ''} onChange={(e) => setDraft({ ...draft, cycle_id: e.target.value || null })} className={inputClass}>
                  <option value="">cycle…</option>{cycles.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </NativeSelect>
              )}
              <Input type="number" placeholder="pos" value={draft.position ?? ''} onChange={(e) => setDraft({ ...draft, position: Number(e.target.value) })} className={cn(inputClass, "!w-[56px]")} />
              <Button disabled={busy || !draft.code || !draft.label || (tab === 'levels' && !draft.cycle_id)}
                onClick={() => void guard(() => saveRef(tab, draft))}>
                + Ajouter
              </Button>
            </div>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground mt-2">
          Les codes sont des clés uniques stables (utilisées par l'extraction). Modifier un code déjà référencé par des programmes peut désaligner l'attribution.
        </p>
      </main>
    </>
  );
}

function RowHead({ tab }: { tab: Table }) {
  const cols = ['CODE', 'LIBELLÉ', ...(tab === 'subjects' ? ['NATURE'] : tab === 'levels' ? ['CYCLE'] : []), 'POS', ''];
  return (
    <div className={cn("grid gap-[6px] py-[5px] px-[10px] items-center border-b border-border bg-secondary text-[10px] font-black tracking-[0.6px] text-muted-foreground min-w-[440px]", GRID[tab])}>
      {cols.map((c, i) => <span key={i}>{c}</span>)}
    </div>
  );
}

function RowEdit({ tab, row, cycles, busy, readOnly, onSave, onDelete }: { tab: Table; row: RefRow; cycles: RefRow[]; busy: boolean; readOnly: boolean; onSave: (f: Partial<RefRow>) => void; onDelete: () => void }) {
  const disabled = busy || readOnly;
  return (
    <div className={cn("grid gap-[6px] py-[5px] px-[10px] items-center border-b border-naia-line-soft min-w-[440px]", GRID[tab])}>
      <Input defaultValue={row.code} disabled={disabled} onBlur={(e) => e.target.value !== row.code && e.target.value && onSave({ code: e.target.value })} className={cn(inputClass, "font-mono")} />
      <Input defaultValue={row.label} disabled={disabled} onBlur={(e) => e.target.value !== row.label && e.target.value && onSave({ label: e.target.value })} className={inputClass} />
      {tab === 'subjects' && (
        <NativeSelect defaultValue={row.nature ?? 'discipline'} disabled={disabled} onChange={(e) => onSave({ nature: e.target.value })} className={inputClass}>
          <option value="discipline">discipline</option><option value="transversal">transversal</option>
        </NativeSelect>
      )}
      {tab === 'levels' && (
        <NativeSelect defaultValue={row.cycle_id ?? ''} disabled={disabled} onChange={(e) => onSave({ cycle_id: e.target.value || null })} className={inputClass}>
          <option value="">—</option>{cycles.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </NativeSelect>
      )}
      <Input type="number" defaultValue={row.position} disabled={disabled} onBlur={(e) => Number(e.target.value) !== row.position && onSave({ position: Number(e.target.value) })} className={cn(inputClass, "!w-[56px]")} />
      {!readOnly && (
        <Button type="button" variant="ghost" size="icon" disabled={busy} title="Supprimer" onClick={onDelete} className="w-6 h-6 min-h-0 text-naia-error">×</Button>
      )}
    </div>
  );
}
